import React, { useMemo } from "react";

import { useUser } from "../../../../pages/Home/UserContext";

import TutorialHero from "../components/TutorialHero";
import TutorialSearchBar from "../components/TutorialSearchBar";
import TutorialSidebar from "../components/TutorialSidebar";
import TutorialMobileMenu from "../components/TutorialMobileMenu";
import TutorialIntro from "../components/TutorialIntro";
import TutorialSection from "../components/TutorialSection";
import TutorialFaq from "../components/TutorialFaq";
import TutorialSupportCard from "../components/TutorialSupportCard";
import TutorialFeedbackCard from "../components/TutorialFeedbackCard";
import TutorialLightbox from "../components/TutorialLightbox";
import TutorialSkeleton from "../components/TutorialSkeleton";
import TutorialErrorState from "../components/TutorialErrorState";
import TutorialEmptyState from "../components/TutorialEmptyState";

import usePublicTutorial from "../hooks/usePublicTutorial";
import useTutorialSearch from "../hooks/useTutorialSearch";
import useTutorialNavigation from "../hooks/useTutorialNavigation";
import useTutorialLightbox from "../hooks/useTutorialLightbox";

import useScrollToTop from "../../../../hooks/useScollToTop";

import { getTutorialRole } from "../utils/tutorialRoleUtils";

export default function TutorialHelpPage() {
  useScrollToTop();
  const { user } = useUser();

  const role = getTutorialRole(user);

  const {
    tutorial,
    sections,
    steps,
    faqs,

    loading,
    refreshing,
    error,
    refresh,
  } = usePublicTutorial({
    role,
  });

  /*
   * Statistiques complètes du tutoriel
   * correspondant au rôle connecté.
   */
  const sectionCount = useMemo(
    () => sections.filter((section) => section.id !== "intro").length,
    [sections],
  );

  const stepCount = useMemo(
    () =>
      Object.values(steps).reduce(
        (total, sectionSteps) =>
          total + (Array.isArray(sectionSteps) ? sectionSteps.length : 0),
        0,
      ),
    [steps],
  );

  /*
   * Recherche dans les étapes,
   * l'introduction et la FAQ.
   */
  const search = useTutorialSearch({
    tutorial,
    sections,
    steps,
    faqs,
  });

  /*
   * Construction du sommaire visible.
   * La FAQ est ajoutée dynamiquement.
   */
  const navigationSections = useMemo(() => {
    const rows = [...search.filteredSections];

    if (search.filteredFaqs.length > 0) {
      rows.push({
        id: "faq",
        label: tutorial?.faq?.label || "Questions fréquentes",
      });
    }

    return rows;
  }, [search.filteredSections, search.filteredFaqs, tutorial?.faq?.label]);

  const navigationSectionIds = useMemo(
    () => navigationSections.map((section) => section.id),
    [navigationSections],
  );

  const { activeSectionId, scrollToSection, getElementId } =
    useTutorialNavigation(navigationSectionIds);

  const lightbox = useTutorialLightbox();

  const firstContentSection = useMemo(
    () => navigationSections.find((section) => section.id !== "intro"),
    [navigationSections],
  );

  if (loading || (refreshing && !tutorial)) {
    return (
      <PageContainer>
        <TutorialSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <TutorialErrorState message={error} onRetry={refresh} />
      </PageContainer>
    );
  }

  if (!tutorial) {
    return (
      <PageContainer>
        <TutorialErrorState
          message="Aucun tutoriel n’est actuellement disponible."
          onRetry={refresh}
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <TutorialHero
          tutorial={tutorial}
          role={role}
          sectionCount={sectionCount}
          stepCount={stepCount}
          faqCount={faqs.length}
        />

        <TutorialSearchBar
          query={search.query}
          onChange={search.setQuery}
          onClear={search.clearSearch}
          hasQuery={search.hasQuery}
          totalResults={search.totalResults}
          stepCount={search.stepCount}
          faqCount={search.faqCount}
        />

        {navigationSections.length ? (
          <TutorialMobileMenu
            sections={navigationSections}
            activeSectionId={activeSectionId}
            onSelect={scrollToSection}
          />
        ) : null}

        <div
          className={
            navigationSections.length
              ? "grid items-stretch gap-6 lg:grid-cols-[290px_minmax(0,1fr)] xl:grid-cols-[320px_minmax(0,1fr)]"
              : ""
          }
        >
          {navigationSections.length ? (
            <TutorialSidebar
              sections={navigationSections}
              activeSectionId={activeSectionId}
              onSelect={scrollToSection}
            />
          ) : null}

          <main className="min-w-0">
            {!search.hasResults ? (
              <TutorialEmptyState
                query={search.query}
                onClear={search.clearSearch}
              />
            ) : (
              <div className="space-y-12">
                {search.introMatches ? (
                  <TutorialIntro
                    intro={tutorial.intro}
                    elementId={getElementId("intro")}
                    onContinue={
                      firstContentSection
                        ? () => scrollToSection(firstContentSection.id)
                        : null
                    }
                  />
                ) : null}

                {search.filteredSections
                  .filter((section) => section.id !== "intro")
                  .map((section, sectionIndex) => (
                    <TutorialSection
                      key={section.id}
                      section={section}
                      steps={search.filteredSteps[section.id] || []}
                      index={sectionIndex}
                      elementId={getElementId(section.id)}
                      searchActive={search.hasQuery}
                      onOpenLightbox={lightbox.openLightbox}
                    />
                  ))}

                {search.filteredFaqs.length > 0 ? (
                  <TutorialFaq
                    tutorialFaq={tutorial.faq}
                    faqs={search.filteredFaqs}
                    elementId={getElementId("faq")}
                    forceOpen={search.hasQuery}
                  />
                ) : null}

                {!search.hasQuery ? (
                  <>
                    <TutorialSupportCard support={tutorial.support} />

                    <TutorialFeedbackCard
                      feedback={tutorial.feedback}
                      tutorialId={tutorial.id}
                      tutorialVersion={tutorial.version}
                    />
                  </>
                ) : null}
              </div>
            )}
          </main>
        </div>
      </div>

      <TutorialLightbox
        isOpen={lightbox.isOpen}
        currentMedia={lightbox.currentMedia}
        currentIndex={lightbox.currentIndex}
        total={lightbox.medias.length}
        hasPrevious={lightbox.hasPrevious}
        hasNext={lightbox.hasNext}
        onPrevious={lightbox.showPrevious}
        onNext={lightbox.showNext}
        onClose={lightbox.closeLightbox}
      />
    </PageContainer>
  );
}

function PageContainer({ children }) {
  return (
    <div className="pt-[130px] min-h-screen bg-slate-50 px-3 py-5 dark:bg-slate-950 sm:px-5 lg:px-7 lg:py-7">
      <div className="mx-auto w-full max-w-[1500px]">{children}</div>
    </div>
  );
}
